import Styles from './EnhanceBrandColors.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight } from 'blaze-ui/icons'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { ColorOption } from './ColorOption/ColorOption'
import { Text } from 'blaze-ui/Text/Text'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { AIJobStatus, BrandKit, BrandVisualEnhancement } from 'types'
import { useState, useEffect, useMemo } from 'react'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useBrandVisualEnhancement } from 'hooks/queries/brandVisualEnhancements/useBrandVisualEnhancement'
import { usePreviewState } from '../PreviewProvider/PreviewProvider'
import { useUpdateBrandVisualEnhancement } from 'hooks/queries/brandVisualEnhancements/useUpdateBrandVisualEnhancement'
import { useUpdateColorPalette } from 'hooks/queries/workspaces/brandVisualKits/useUpdateColorPalette'
import { toHex } from 'flows/common/steps/BrandKitColorsAndFonts/utils'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'

interface Props {
  onNext: ({
    isUsingEnhancedColors,
    selectedEnhancedColorIndex,
  }: {
    isUsingEnhancedColors: boolean
    selectedEnhancedColorIndex?: number | null
  }) => void
  onBack?: () => void
  brandKitId: number
  brandVisualEnhancementId?: number
  selectedEnhancedColorIndex?: number | null
  fromBrandKitConfirm?: boolean
  brandKit?: BrandKit
  enhancement?: BrandVisualEnhancement
}

export const EnhanceBrandColors = ({
  onNext,
  onBack,
  brandKitId,
  selectedEnhancedColorIndex,
  brandVisualEnhancementId: brandVisualEnhancementIdProp,
  fromBrandKitConfirm,
  brandKit,
  enhancement,
}: Props) => {
  const { setColors, setBrandKit, setMedia, colors: previewColors, setColors: setPreviewColors } = usePreviewState()
  const initialSelectedOption = useMemo(
    () => (selectedEnhancedColorIndex !== null && selectedEnhancedColorIndex !== undefined ? 'enhanced' : 'original'),
    [selectedEnhancedColorIndex],
  )
  const [selectedOption, setSelectedOption] = useState<'enhanced' | 'original'>(initialSelectedOption)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(selectedEnhancedColorIndex ?? null)

  const { data: brandKitResponse } = useBrandKit(brandKitId, {
    pollUntilFinal: !fromBrandKitConfirm,
    includeBrandVisualAnalysis: true,
    enabled: !fromBrandKitConfirm,
  })

  const finalBrandKit = brandKit || brandKitResponse?.brand_kit
  const brandVisualEnhancementId =
    finalBrandKit?.brand_visual_kit?.brand_visual_enhancement_id || brandVisualEnhancementIdProp
  const { data: enhancementResponse, isLoading: enhancementLoading } = useBrandVisualEnhancement(
    finalBrandKit?.brand_visual_kit?.id,
    brandVisualEnhancementId ?? undefined,
    {
      pollUntilFinal: !fromBrandKitConfirm,
      enabled: !fromBrandKitConfirm && !!brandVisualEnhancementId,
      keepPreviousData: true,
    },
  )

  const finalEnhancement = enhancement || enhancementResponse?.brand_visual_enhancement

  const { mutate: updateEnhancement } = useUpdateBrandVisualEnhancement(
    finalBrandKit?.brand_visual_kit?.id,
    brandVisualEnhancementId ?? undefined,
  )

  const { mutateAsync: updateColorPalette } = useUpdateColorPalette({
    brandVisualKitId: finalBrandKit?.brand_visual_kit?.id as number,
    id: finalBrandKit?.brand_visual_kit?.primary_color_palette.id as number,
  })

  const enhancedPalettes = finalEnhancement?.result?.suggested_color_palettes
  const enhancedColors = useMemo(
    (): string[] => enhancedPalettes?.[selectedIndex ?? 0]?.colors || [],
    [enhancedPalettes, selectedIndex],
  )

  const enhancementStatus = finalEnhancement?.status

  const persistEnhancedColors = (colors: string[], index: number) => {
    if (!brandVisualEnhancementId) return

    updateEnhancement({
      result: {
        ...finalEnhancement?.result,
        suggested_color_palettes: enhancedPalettes?.map((palette, paletteIndex) => {
          if (paletteIndex === index) {
            return {
              ...palette,
              colors,
            }
          }
          return palette
        }),
      },
    })
  }

  const persistOriginalColors = async (colors: string[]) => {
    await updateColorPalette({
      colors: colors.map((c) => ({ hex: toHex(c), name: undefined })),
    })
  }

  const originalColors = useMemo(
    () => finalBrandKit?.brand_visual_kit?.primary_color_palette.colors.map((c: any) => c.hex) || [],
    [finalBrandKit?.brand_visual_kit?.primary_color_palette.colors],
  )

  useEffect(() => {
    if (finalBrandKit) {
      setBrandKit(finalBrandKit)
      setMedia(finalBrandKit.media_files[0])
    }
  }, [finalBrandKit, setBrandKit, setMedia])

  useEffect(() => {
    setColors(selectedOption === 'enhanced' ? enhancedColors : originalColors)
  }, [selectedOption, enhancedColors, originalColors, setColors])

  if (
    !fromBrandKitConfirm &&
    brandVisualEnhancementId &&
    (enhancementLoading || enhancementStatus === AIJobStatus.Pending || enhancementStatus === AIJobStatus.Started)
  ) {
    return (
      <MultiStepStepLayout
        title="Analyzing your brand..."
        scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
        contentClassName={VirtualMarketerOnboardingStyles.content}
      >
        <div className={Styles.loadingContainer}>
          <Text color="var(--dark-60)">We're analyzing your brand colors…</Text>
        </div>
      </MultiStepStepLayout>
    )
  }

  if (brandVisualEnhancementId && enhancementStatus === AIJobStatus.Failed) {
    return (
      <MultiStepStepLayout
        title="Something went wrong"
        scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
        contentClassName={VirtualMarketerOnboardingStyles.content}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <Button variant="tertiary" size="xl" onPress={onBack} frontIcon={ArrowLeft} aria-label="Back">
                  Back
                </Button>
                <Button size="xl" onPress={() => onNext({ isUsingEnhancedColors: false })} aria-label="Continue">
                  Continue with current colors
                </Button>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <div className={Styles.errorContainer}>
            <Text color="var(--dark-60)">
              We couldn't analyze your brand colors. You can continue with your current colors or go back and try again.
            </Text>
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  }

  return (
    <MultiStepStepLayout
      title="Select your colors for social posts"
      scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
      contentClassName={VirtualMarketerOnboardingStyles.content}
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <ResponsiveDesign.Desktop>
                {onBack ? (
                  <Button
                    variant="tertiary"
                    size="xl"
                    onPress={() => {
                      setSelectedOption(initialSelectedOption)
                      setSelectedIndex(selectedEnhancedColorIndex ?? null)
                      onBack()
                    }}
                    frontIcon={ArrowLeft}
                    aria-label="Back"
                    id="enhance-brand-colors-back"
                  >
                    Back
                  </Button>
                ) : (
                  <div />
                )}
                <Button
                  size="xl"
                  endIcon={ArrowRight}
                  onPress={() =>
                    onNext({
                      isUsingEnhancedColors: selectedOption === 'enhanced',
                      selectedEnhancedColorIndex: selectedIndex,
                    })
                  }
                  aria-label="Confirm colors"
                  id="enhance-brand-colors-next"
                >
                  Confirm Colors
                </Button>
              </ResponsiveDesign.Desktop>
              <ResponsiveDesign.Mobile>
                <Button
                  size="xl"
                  endIcon={ArrowRight}
                  onPress={() =>
                    onNext({
                      isUsingEnhancedColors: selectedOption === 'enhanced',
                      selectedEnhancedColorIndex: selectedIndex,
                    })
                  }
                  aria-label="Confirm colors"
                  id="enhance-brand-colors-next"
                  fullWidth
                >
                  Confirm Colors
                </Button>
              </ResponsiveDesign.Mobile>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.root}>
          <div className={Styles.colorOptions}>
            <ColorOption
              colors={originalColors}
              title="Here's what we pulled from your site."
              badgeText="Your Website"
              ordinal={1}
              isSelected={selectedOption === 'original'}
              onSelect={() => {
                setSelectedOption('original')
                setSelectedIndex(null)
              }}
              onPersistColors={persistOriginalColors}
              previewColors={previewColors}
              setPreviewColors={setPreviewColors}
            />

            {enhancedPalettes?.map((palette, index) => {
              let title = 'These colors feel just right for your brand.'
              if (index === 1) title = 'These colors also feel like a good match.'
              if (index === 2) title = 'These colors feel like a great option to explore.'

              return (
                <>
                  {brandVisualEnhancementId && enhancedColors.length > 0 && (
                    <ColorOption
                      colors={palette.colors}
                      title={title}
                      key={index}
                      badgeText={index === 0 ? 'Best Option' : undefined}
                      ordinal={index + 2}
                      description={palette.reasoning}
                      isSelected={selectedOption === 'enhanced' && selectedIndex === index}
                      onSelect={() => {
                        setSelectedOption('enhanced')
                        setSelectedIndex(index)
                      }}
                      onPersistColors={(newColors: string[]) => persistEnhancedColors(newColors, index)}
                      previewColors={previewColors}
                      setPreviewColors={setPreviewColors}
                    />
                  )}
                </>
              )
            })}
          </div>
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
